import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { EyeIcon, TrashIcon, EyeSlashIcon, ArchiveBoxXMarkIcon, MagnifyingGlassIcon, PrinterIcon } from '@heroicons/react/24/outline';
import api from '../utils/api';
import useSocket from '../hooks/useSocket';

// Kargo firmaları artık Kargonomi API'den dinamik olarak geliyor
const statusTranslations = { 
    pending: 'Hazırlanıyor',
    processing: 'İşleniyor',
    confirmed: 'Onaylandı',
    shipped: 'Kargolandı', 
    delivered: 'Teslim Edildi', 
    cancelled: 'İptal Edildi',
    shipping_failed: 'Kargo Hatası',
    delivery_failed: 'Teslim Başarısız',
    cancellation_requested: 'İptal Talebi',
    lost: 'Kayıp',
    return_requested: 'İade Talebi Alındı',
    return_approved: 'İade Onaylandı',
    return_rejected: 'İade Reddedildi',
    return_shipped: 'İade Kargoda',
    return_received: 'İade Teslim Alındı',
    return_completed: 'İade Tamamlandı'
};
const statusColors = { 
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    processing: 'bg-blue-100 text-blue-800 border-blue-200',
    confirmed: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    shipped: 'bg-brand-green bg-opacity-20 text-brand-green border-brand-green border-opacity-30', 
    delivered: 'bg-green-100 text-green-800 border-green-200', 
    cancelled: 'bg-red-100 text-red-800 border-red-200',
    shipping_failed: 'bg-red-200 text-red-900 border-red-300',
    delivery_failed: 'bg-orange-200 text-orange-900 border-orange-300',
    cancellation_requested: 'bg-yellow-200 text-yellow-900 border-yellow-300',
    lost: 'bg-gray-200 text-gray-900 border-gray-300',
    return_requested: 'bg-orange-100 text-orange-800 border-orange-200',
    return_approved: 'bg-teal-100 text-teal-800 border-teal-200',
    return_rejected: 'bg-red-100 text-red-800 border-red-200',
    return_shipped: 'bg-purple-100 text-purple-800 border-purple-200',
    return_received: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    return_completed: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};
const statusOptions = [
    'pending',
    'processing',
    'confirmed',
    'shipped', 
    'delivered', 
    'cancelled',
    'shipping_failed',
    'delivery_failed',
    'cancellation_requested',
    'lost',
    'return_requested',
    'return_approved',
    'return_rejected',
    'return_shipped',
    'return_received',
    'return_completed'
];

const TrackingInfoModal = ({ order, onSave, closeModal }) => {
    const [tracking, setTracking] = useState(order.tracking_number || '');
    const [company, setCompany] = useState('');
    const [loadingTracking, setLoadingTracking] = useState(false);
    const [notes, setNotes] = useState('');
    const [shippingProviders, setShippingProviders] = useState([]);
    const [loadingShippingProviders, setLoadingShippingProviders] = useState(false);
    const { token } = useAuth();

    // Modal açıldığında kargo firmalarını otomatik yükle ve müşterinin seçtiğini ayarla
    useEffect(() => {
        console.log('=== MODAL AÇILDI - ORDER DATA ===');
        console.log('Tam order verisi:', order);
        console.log('selected_shipping_provider:', order.selected_shipping_provider);
        console.log('kargonomi_shipment_id:', order.kargonomi_shipment_id);
        console.log('shipping_company:', order.shipping_company);
        
        // Modal açılır açılmaz kargo firmalarını yükle
        const loadInitialShippingProviders = async () => {
            // Önce müşterinin seçtiği kargo firmasını ayarla (geçici olarak)
            if (order.selected_shipping_provider) {
                setCompany(order.selected_shipping_provider);
                console.log('Müşterinin seçtiği kargo (geçici):', order.selected_shipping_provider);
            } else if (order.shipping_company) {
                setCompany(order.shipping_company);
                console.log('Mevcut kargo firması (geçici):', order.shipping_company);
            }
            
            // Sonra Kargonomi'den güncel listeyi çek
            await fetchShippingProviders();
        };
        
        loadInitialShippingProviders();
    }, [order]);

    // Kargonomi API'den kargo firmalarını çek
    const fetchShippingProviders = async () => {
        setLoadingShippingProviders(true);
        try {
            console.log('[Modal] Sipariş detayları çekiliyor...');
            
            // Önce sipariş detaylarını tam olarak çek
            const detailedOrder = await api(`/api/orders/${order.id}`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });
            
            console.log('[Modal] Detaylı sipariş verisi:', detailedOrder);
            console.log('[Modal] Order items:', detailedOrder.order_items);

            // Sipariş adres bilgilerini parse et
            let shippingAddress = {};
            if (detailedOrder.shipping_address) {
                if (typeof detailedOrder.shipping_address === 'string') {
                    try {
                        shippingAddress = JSON.parse(detailedOrder.shipping_address);
                    } catch (e) {
                        console.warn('Adres parse hatası:', e);
                        shippingAddress = {};
                    }
                } else {
                    shippingAddress = detailedOrder.shipping_address;
                }
            }

            // Sipariş ürünlerini hazırla
            const cartItems = detailedOrder.order_items || [];

            console.log('[Modal] Sipariş detayları:', {
                orderId: order.id,
                orderItems: cartItems,
                shippingAddress: shippingAddress,
                city: shippingAddress.city,
                district: shippingAddress.district,
                itemsCount: cartItems.length
            });

            // CartItems'ı doğru formatta hazırla
            const formattedCartItems = cartItems.map(item => {
                console.log('[Modal] İşlenen ürün:', item);
                return {
                    id: item.product_id || item.id,
                    name: item.products?.name || 'Ürün',
                    price: parseFloat(item.price_at_purchase || item.price || 0),
                    quantity: parseInt(item.quantity || 1),
                    desi: parseFloat(item.products?.desi || 0.5),
                    main_image_url: item.products?.main_image_url || '',
                    variant: {
                        id: item.variant_id,
                        size: item.variant_details_at_purchase?.size || 'Standart',
                        color: item.variant_details_at_purchase?.color || 'Varsayılan',
                        price_override: item.variant_details_at_purchase?.price_override
                    }
                };
            });

            console.log('[Modal] Formatlanmış cartItems:', formattedCartItems);

            // Kargo hesaplama API'sini çağır
            const response = await api.post('/api/shipping-calculator/calculate', {
                shippingAddress: {
                    full_name: shippingAddress.full_name || order.shipping_name,
                    address_line: shippingAddress.address_line || shippingAddress.address,
                    city: shippingAddress.city,
                    district: shippingAddress.district,
                    phone: shippingAddress.phone,
                    email: order.guest_email || 'admin@minelakids.com'
                },
                cartItems: formattedCartItems
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('[Modal] Kargo API yanıtı:', response);
            console.log('[Modal] Response success:', response.success);
            console.log('[Modal] Response data:', response.data);
            console.log('[Modal] Shipping options:', response.data?.shippingOptions);

            if (response.success && response.data?.shippingOptions && response.data.shippingOptions.length > 0) {
                const options = response.data.shippingOptions;
                setShippingProviders(options);
                
                // Müşterinin seçtiği kargo firmasını Kargonomi listesinde bul
                const customerSelectedProvider = options.find(opt => 
                    opt.name === order.selected_shipping_provider || 
                    opt.name.toLowerCase().includes(order.selected_shipping_provider?.toLowerCase() || '') ||
                    order.selected_shipping_provider?.toLowerCase().includes(opt.name.toLowerCase() || '')
                );
                
                // Mevcut seçili kargo firmasını kontrol et
                const currentProvider = options.find(opt => 
                    opt.name === company || 
                    opt.name.toLowerCase().includes(company?.toLowerCase() || '')
                );
                
                if (customerSelectedProvider) {
                    // Müşterinin seçtiği kargo firması Kargonomi'de bulundu
                    setCompany(customerSelectedProvider.name);
                    console.log('[Modal] ✅ Müşterinin seçtiği kargo firması bulundu ve ayarlandı:', customerSelectedProvider.name);
                } else if (currentProvider) {
                    // Mevcut seçili kargo firması geçerli
                    setCompany(currentProvider.name);
                    console.log('[Modal] ✅ Mevcut kargo firması korundu:', currentProvider.name);
                } else if (options.length > 0) {
                    // Hiçbiri bulunamadıysa ilk seçeneği kullan
                    setCompany(options[0].name);
                    console.log('[Modal] ⚠️ Müşterinin seçtiği kargo bulunamadı, ilk seçenek kullanılıyor:', options[0].name);
                }
                
                console.log(`[Modal] ${options.length} kargo seçeneği yüklendi`);
                console.log('[Modal] Müşterinin orijinal seçimi:', order.selected_shipping_provider);
                console.log('[Modal] Final seçim:', company);
            } else {
                console.error('[Modal] Kargo API hatası:', {
                    success: response.success,
                    data: response.data,
                    error: response.error,
                    message: response.message
                });
                
                if (response.data?.error) {
                    throw new Error(response.data.error);
                } else if (response.error) {
                    throw new Error(response.error);
                } else {
                    throw new Error('Kargo seçeneği bulunamadı - API yanıtı boş');
                }
            }
        } catch (error) {
            console.error('[Modal] Kargo seçenekleri yüklenirken hata:', error);
            toast.error('Kargo seçenekleri yüklenemedi: ' + error.message);
            
            // Hata durumunda varsayılan listesi kullan
            setShippingProviders([]);
        } finally {
            setLoadingShippingProviders(false);
        }
    };

    const fetchTrackingFromKargonomi = async () => {
        // Zaten kargo oluşturulmuş mu kontrol et
        if (order.kargonomi_shipment_id) {
            toast.warning('⚠️ Bu sipariş için zaten kargo oluşturulmuş! Kargonomi ID: ' + order.kargonomi_shipment_id);
            return;
        }
        
        setLoadingTracking(true);
        try {
            console.log('=== KARGO OLUŞTURMA BAŞLADI ===');
            console.log('Sipariş ID:', order.id);
            console.log('Kargo yönetimi sistemini kullanarak shipment oluşturuluyor...');
            
            // Kargo yönetimindeki aynı endpoint'i kullan - YÖNETİCİNİN SEÇTİĞİ KARGO FİRMASI İLE
            console.log(`[OrderManagement] Kargo oluşturma isteği gönderiliyor: ${order.id}`);
            console.log(`[OrderManagement] Yöneticinin seçtiği kargo firması: ${company}`);
            
            // Seçilen kargo firmasının ID'sini bul
            const selectedProvider = shippingProviders.find(provider => provider.name === company);
            const selectedProviderId = selectedProvider ? selectedProvider.id : null;
            
            console.log(`[OrderManagement] Seçilen kargo firması: ${company}`);
            console.log(`[OrderManagement] Seçilen kargo ID: ${selectedProviderId}`);
            
            const response = await api.post(`/api/orders/${order.id}/auto-shipping`, {
                // Backend'in beklediği parametre adı
                selectedProviderId: selectedProviderId
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log(`[OrderManagement] Kargo oluşturma yanıtı:`, response);
            
            console.log('=== AUTO-SHIPPING RESPONSE ===');
            console.log('Response:', response);
            console.log('Success:', response.success);
            console.log('Message:', response.message);
            console.log('Error:', response.error);
            
            if (response.success) {
                console.log('✅ Kargo başarıyla oluşturuldu!');
                console.log('Response data:', response.data);
                console.log('Shipment:', response.shipment);
                console.log('Tracking code:', response.shipment?.shipping_webservice_tracking_code);
                
                // Sadece gerçek referans numarasını al (taslak numaralar değil)
                const kargonomiReference = response.shipment?.shipping_webservice_order_id;
                const realTrackingCode = response.shipment?.shipping_webservice_tracking_code;
                
                // Önce gerçek takip numarası, yoksa referans numarası
                const trackingNumber = realTrackingCode || kargonomiReference;
                
                if (trackingNumber) {
                    setTracking(trackingNumber);
                    if (realTrackingCode) {
                        toast.success('🎉 Gerçek takip numarası alındı! Kaydet butonuna basarak işlemi tamamlayın.');
                    } else if (kargonomiReference) {
                        toast.success('📋 Kargonomi referans numarası alındı! Bu numarayla kargo takip edilebilir. Kaydet butonuna basın.');
                        
                        // YENİ Referans polling servisini başlat
                        try {
                            const pollingResponse = await api.post(`/api/reference-polling/start/${order.id}`, {
                                shipmentId: response.data?.kargonomiShipmentId || response.kargonomiShipmentId
                            }, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            
                            if (pollingResponse.success) {
                                console.log(`[OrderManagement] ✅ Sipariş ${order.id} için referans polling başlatıldı`);
                                toast.info('🤖 Otomatik referans polling sistemi başlatıldı! 30 saniyede bir kontrol edilecek.');
                            }
                        } catch (pollingError) {
                            console.warn('[OrderManagement] ⚠️ Referans polling başlatılamadı:', pollingError.message);
                            // Polling başlamazsa da devam et, çünkü kargo oluşturuldu
                        }
                    } else {
                        // Referans numarası henüz gelmedi - Taslak durumda
                        toast.warning('⚠️ Kargo taslak olarak oluşturuldu. Kargonomi panelinden onaylamanız gerekiyor.');
                        
                        // Taslak için otomatik takip başlatma
                        console.log(`[OrderManagement] Sipariş ${order.id} taslak olarak oluşturuldu, manuel onay gerekiyor`);
                    }
                    
                    // Otomatik takip süreci aktif mi kontrol et
                    if (response.tracking_polling?.active) {
                        toast.info(`🔄 ${response.tracking_polling.message} Arka planda otomatik takip numarası alınmaya devam ediliyor.`);
                    }
                } else if (response.requiresManualConfirmation) {
                    // Özel durum: Yeterli bakiye yok
                    if (response.error_message && response.error_message.includes('Yeterli Bakiye')) {
                        toast.error('❌ Kargonomi Bakiye Hatası: ' + response.error_message + '\n\n🔧 Çözüm: Kargonomi panelinden bakiye yükleyin veya manuel takip numarası girin.');
                    } else {
                        toast.warning('⚠️ Kargo draft oluşturuldu. Kargonomi panelinden onaylayın ve takip numarasını manuel girin.\n\nHata: ' + (response.error_message || 'Bilinmiyor'));
                    }
                } else if (response.warning) {
                    toast.warning('⚠️ ' + response.warning + '\n\nDetay: ' + (response.details || response.error_message || 'Bilinmiyor'));
                } else if (response.error_message) {
                    toast.error('❌ Kargonomi hatası: ' + response.error_message);
                } else {
                    toast.info('ℹ️ Kargo oluşturuldu. Takip numarasını manuel girebilirsiniz.');
                }
                
                // Modal açık kalsın
            } else {
                console.log('❌ Kargo oluşturulamadı');
                toast.error('❌ Kargo oluşturulamadı: ' + (response.error || response.error_message || 'Bilinmeyen hata'));
            }
            
        } catch (error) {
            console.error('=== KARGO OLUŞTURMA HATASI ===');
            console.error('Error:', error);
            console.error('Error message:', error.message);
            
            // Hata mesajını güvenli şekilde al
            let errorMessage = 'Bilinmeyen hata oluştu';
            if (error && error.message) {
                errorMessage = error.message;
            } else if (error && error.response) {
                if (error.response.details) {
                    errorMessage = error.response.details;
                } else if (error.response.error) {
                    errorMessage = error.response.error;
                } else if (typeof error.response === 'string') {
                    errorMessage = error.response;
                }
            }
            
            toast.error('Kargo oluşturulurken hata: ' + errorMessage);
        } finally {
            setLoadingTracking(false);
        }
    };

    return (
        <div className="bg-gradient-to-br from-white via-gray-50 to-white rounded-none sm:rounded-3xl p-6 sm:p-8 md:p-10 w-full sm:w-auto sm:min-w-[500px] sm:max-w-[700px] mx-auto shadow-2xl border border-gray-100 h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto">
            {/* Premium Başlık */}
            <div className="text-center mb-6 sm:mb-8 md:mb-10 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-brand-green/5 via-blue-500/5 to-purple-500/5 rounded-2xl -m-4"></div>
                <div className="relative">
                    <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-gradient-to-br from-brand-green via-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl transform rotate-3 hover:rotate-0 transition-transform duration-300">
                        <svg className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                        </svg>
                    </div>
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent mb-2">Kargo Yönetimi</h2>
                    <div className="flex items-center justify-center space-x-2 mb-3">
                        <div className="h-px bg-gradient-to-r from-transparent via-brand-green to-transparent flex-1"></div>
                        <span className="text-sm font-medium text-brand-green px-3 py-1 bg-brand-green/10 rounded-full">Premium Panel</span>
                        <div className="h-px bg-gradient-to-r from-transparent via-brand-green to-transparent flex-1"></div>
                    </div>
                    <p className="text-sm md:text-base text-gray-600">Sipariş: <span className="font-mono font-bold text-brand-green bg-brand-green/10 px-3 py-1 rounded-lg">MINELA-{order.order_number}</span></p>
                </div>
            </div>


            {/* Alıcı Adres Bilgileri */}
            <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl shadow-sm">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-green-800">Teslimat Adresi</p>
                        <p className="text-xs text-green-600">Kargonun gönderileceği adres</p>
                    </div>
                </div>
                {(() => {
                    let addressObject = null;
                    if (order.shipping_address && typeof order.shipping_address === 'string') {
                        try {
                            addressObject = JSON.parse(order.shipping_address);
                        } catch (e) {
                            console.error("Adres verisi JSON formatında değil:", order.shipping_address);
                        }
                    } else if (order.shipping_address) {
                        addressObject = order.shipping_address;
                    }

                    return addressObject ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Alıcı:</span>
                                <span className="text-gray-800">{addressObject.full_name || addressObject.name || order.shipping_name || 'Belirtilmemiş'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">Telefon:</span>
                                <span className="text-gray-800">{addressObject.phone || 'Belirtilmemiş'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">İl:</span>
                                <span className="text-gray-800">{addressObject.city || 'Belirtilmemiş'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="font-medium text-gray-700">İlçe:</span>
                                <span className="text-gray-800">{addressObject.district || 'Belirtilmemiş'}</span>
                            </div>
                            <div className="md:col-span-2 mt-2 p-3 bg-white rounded-lg border">
                                <span className="font-medium text-gray-700 block mb-1">Açık Adres:</span>
                                <span className="text-gray-800">{addressObject.address_line || addressObject.address || 'Belirtilmemiş'}</span>
                            </div>
                        </div>
                    ) : (
                        <p className="text-gray-500 text-sm">Bu sipariş için teslimat adresi bilgisi bulunamadı.</p>
                    );
                })()}
            </div>

            {/* Müşterinin Seçtiği Kargo Firması */}
            {order.selected_shipping_provider && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl shadow-sm">
                    <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-blue-800">Müşteri Tercihi</p>
                            <p className="text-xs text-blue-600">{order.selected_shipping_provider}</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-6 sm:space-y-8">
                {/* Kargo Firması Seçimi */}
                <div className="group">
                    <label className="block text-sm sm:text-base font-bold text-gray-700 mb-3 flex items-center space-x-2">
                        <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span>Kargo Firması</span>
                    </label>
                    
                    <div className="relative">
                        {/* Kargonomi Entegreli Kargo Seçimi */}
                        {loadingShippingProviders ? (
                            <div className="flex items-center justify-center p-4 border-2 border-gray-200 rounded-2xl bg-gray-50">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-brand-green border-t-transparent mr-3"></div>
                                <span className="text-gray-600">Kargo firmaları yükleniyor...</span>
                            </div>
                        ) : (
                            <select 
                                value={company || ''} 
                                onChange={e => {
                                    setCompany(e.target.value);
                                    console.log('[Modal] Kargo firması değiştirildi:', e.target.value);
                                }} 
                                className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green/20 focus:border-brand-green transition-all text-sm sm:text-base bg-white shadow-sm hover:shadow-md group-hover:border-brand-green/50"
                            >
                                {shippingProviders.length > 0 ? (
                                    shippingProviders.map(provider => (
                                        <option key={provider.id} value={provider.name}>
                                            {provider.name} - ₺{provider.price?.toFixed(2) || '0.00'} 
                                            {provider.isFreeShipping && ' (Ücretsiz)'}
                                            {provider.estimatedDays && ` (${provider.estimatedDays} gün)`}
                                        </option>
                                    ))
                                ) : (
                                    <option value="">Kargo firması bulunamadı</option>
                                )}
                            </select>
                        )}
                        
                        {/* Kargonomi Bilgi Notu */}
                        <div className="mt-2 p-2 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                            <p className="text-xs text-green-700 flex items-center">
                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Kargonomi API'den güncel fiyatlar ve kargo firmaları
                            </p>
                        </div>
                    </div>
                </div>

                {/* Takip Numarası */}
                <div className="group">
                    <label className="block text-sm sm:text-base font-bold text-gray-700 mb-3 flex items-center space-x-2">
                        <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span>Takip Numarası</span>
                    </label>
                    
                    {/* Kargonomi'den Al Butonu */}
                    <div className="mb-4">
                        <button
                            onClick={fetchTrackingFromKargonomi}
                            disabled={loadingTracking}
                            className="w-full flex items-center justify-center space-x-3 bg-gradient-to-r from-brand-green via-emerald-500 to-teal-600 text-white px-4 py-4 rounded-2xl hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] text-sm font-semibold"
                        >
                            {loadingTracking ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                    <span>Sistem Entegrasyonu Çalışıyor...</span>
                                </>
                            ) : (
                                <>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    <span>Otomatik Referans Numarası Al</span>
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                </>
                            )}
                        </button>
                    </div>
                    
                    <input 
                        type="text" 
                        value={tracking} 
                        onChange={(e) => setTracking(e.target.value)} 
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green/20 focus:border-brand-green transition-all font-mono text-sm sm:text-base bg-white shadow-sm hover:shadow-md group-hover:border-brand-green/50"
                        placeholder="Takip numarasını girin veya otomatik alın..."
                    />
                    <p className="text-xs sm:text-sm text-gray-500 mt-3 flex items-center space-x-2">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>Manuel giriş yapabilir veya sistem entegrasyonu kullanabilirsiniz</span>
                    </p>
                </div>

                {/* Admin Notları */}
                <div className="group">
                    <label className="block text-sm sm:text-base font-bold text-gray-700 mb-3 flex items-center space-x-2">
                        <svg className="w-5 h-5 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>Admin Notları</span>
                        <span className="text-xs text-gray-500">(İsteğe Bağlı)</span>
                    </label>
                    <textarea 
                        value={notes} 
                        onChange={(e) => setNotes(e.target.value)} 
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-brand-green/20 focus:border-brand-green transition-all text-sm sm:text-base bg-white shadow-sm hover:shadow-md group-hover:border-brand-green/50 resize-none"
                        placeholder="Kargo ile ilgili özel notlarınızı buraya yazabilirsiniz..."
                        rows="3"
                    />
                </div>
            </div>

            {/* Premium Butonlar */}
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-6 mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-gradient-to-r from-gray-200 via-gray-300 to-gray-200">
                <button 
                    onClick={closeModal} 
                    className="flex-1 px-6 sm:px-8 py-4 sm:py-5 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 rounded-2xl transition-all font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl transform hover:scale-[1.02] border border-gray-300"
                >
                    <span className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>İptal Et</span>
                    </span>
                </button>
                <button 
                    onClick={async () => { 
                        try {
                            // Önce sipariş durumunu güncelle
                            console.log('Admin notları kaydediliyor:', notes);
                            await onSave(order.id, { 
                                status: 'shipped', 
                                tracking_number: tracking, 
                                shipping_company: company,
                                admin_notes: notes
                            });
                            
                            // Duplicate kargo oluşturma kaldırıldı - sadece modal'daki buton kullanılacak
                            
                            closeModal(); 
                        } catch (error) {
                            console.error('Kaydet işlemi hatası:', error);
                            toast.error('Kaydetme sırasında hata oluştu: ' + error.message);
                        }
                    }} 
                    className="flex-1 px-6 sm:px-8 py-4 sm:py-5 bg-gradient-to-r from-brand-green via-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-2xl transition-all font-bold shadow-xl hover:shadow-2xl transform hover:scale-[1.02] text-sm sm:text-base"
                >
                    <span className="flex items-center justify-center space-x-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="hidden sm:inline">Kaydet ve Kargolandı İşaretle</span>
                        <span className="sm:hidden">Kargolandı Yap</span>
                    </span>
                </button>
                
            </div>
        </div>
    );
};

const SearchResultsModal = ({ results, searchTerm, closeModal, viewOrderDetails }) => {
    return (
        <div className="p-4 max-w-2xl w-full">
            <h2 className="text-xl font-bold mb-4">Arama Sonuçları: <span className="text-brand-green">"{searchTerm}"</span></h2>
            {results.length === 0 ? (
                <p>Bu arama için sonuç bulunamadı.</p>
            ) : (
                <div className="space-y-4 max-h-[70vh] overflow-y-auto">
                    {results.map(order => (
                        <div key={order.id} className="p-4 border rounded-lg bg-gray-50 flex justify-between items-center">
                            <div>
                                <p className="font-bold">Sipariş No: MINELA-{order.order_number}</p>
                                <p>Müşteri: {order.shipping_name}</p>
                                <p>Tarih: {new Date(order.created_at).toLocaleDateString('tr-TR')}</p>
                                <p>Tutar: ₺{parseFloat(order.total_price).toFixed(2)}</p>
                            </div>
                            <button onClick={() => { closeModal(); viewOrderDetails(order.id); }} className="bg-gradient-to-r from-brand-green to-brand-green-light text-white px-4 py-2 rounded-xl text-sm hover:from-brand-green-light hover:to-brand-green transition-all duration-300 transform hover:scale-105 shadow-lg">Detayları Gör</button>
                        </div>
                    ))}
                </div>
            )}
            <button onClick={closeModal} className="mt-6 w-full bg-gray-200 hover:bg-gray-300 py-2 rounded-md">Kapat</button>
        </div>
    );
};

function OrderManagementPanel({ openModal, closeModal }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token } = useAuth();
    
    const [showHidden, setShowHidden] = useState(false);
    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30); // 30 gun oncesinden baslat
        return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        const date = new Date();
        date.setDate(date.getDate() + 1); // Yarin dahil et
        return date.toISOString().split('T')[0];
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [autoRefresh, setAutoRefresh] = useState(false);


    const viewOrderDetails = useCallback(async (orderId) => {
        try {
            const detailedOrder = await api(`/api/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
            console.log('Sipariş detayları:', detailedOrder);
            console.log('Admin notları:', detailedOrder.admin_notes);
            
            // NİHAİ ÇÖZÜM: Gelen metni (string) objeye dönüştürüyoruz.
            let addressObject = null;
            if (detailedOrder.shipping_address && typeof detailedOrder.shipping_address === 'string') {
                try {
                    addressObject = JSON.parse(detailedOrder.shipping_address);
                } catch (e) {
                    console.error("Adres verisi JSON formatında değil:", detailedOrder.shipping_address);
                }
            } else if (detailedOrder.shipping_address) {
                // Eğer zaten bir obje ise doğrudan kullan
                addressObject = detailedOrder.shipping_address;
            }

            // İade bilgilerini kontrol et
            const hasReturnInfo = detailedOrder.returns && detailedOrder.returns.length > 0;
            const returnData = hasReturnInfo ? detailedOrder.returns[0] : null;

            openModal(
                <div className="p-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold">Sipariş Detayı</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[detailedOrder.status]}`}>
                            {statusTranslations[detailedOrder.status]}
                        </span>
                    </div>
                    
                    <div className="mb-4 p-4 border rounded-lg bg-gray-50 text-sm">
                        <div className="flex justify-between items-start mb-2">
                            <span className="font-semibold text-gray-700">Sipariş No:</span>
                            <span className="font-mono">MINELA-{detailedOrder.order_number}</span>
                        </div>
                        <div className="flex justify-between items-start mb-2">
                            <span className="font-semibold text-gray-700">Tarih:</span>
                            <span>{new Date(detailedOrder.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                        <div className="flex justify-between items-start">
                            <span className="font-semibold text-gray-700">Toplam:</span>
                            <span className="font-bold">₺{parseFloat(detailedOrder.total_price).toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Teslimat Adresi */}
                    <div className="mb-4 p-6 border-2 border-brand-green border-opacity-20 rounded-2xl bg-gradient-to-br from-brand-header to-gray-50 text-sm shadow-lg">
                        <h4 className="font-bold text-brand-green mb-3 flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Teslimat Adresi
                        </h4>
                        {addressObject ? (
                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className='font-semibold text-gray-700'>Adres Başlığı:</span>
                                    <span>{addressObject.address_title || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className='font-semibold text-gray-700'>Alıcı:</span>
                                    <span>{addressObject.full_name || addressObject.name || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className='font-semibold text-gray-700'>Telefon:</span>
                                    <span>{addressObject.phone || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className='font-semibold text-gray-700'>İl:</span>
                                    <span>{addressObject.city || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className='font-semibold text-gray-700'>İlçe:</span>
                                    <span>{addressObject.district || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className='font-semibold text-gray-700'>Mahalle:</span>
                                    <span>{addressObject.neighborhood || 'Belirtilmemiş'}</span>
                                </div>
                                <div className="mt-3 p-3 bg-white rounded-lg border">
                                    <span className='font-semibold text-gray-700 block mb-1'>Açık Adres:</span>
                                    <span className="text-gray-800">{addressObject.address_line || addressObject.address || 'Belirtilmemiş'}</span>
                                </div>
                            </div>
                        ) : (
                            <p className="text-gray-500">Bu sipariş için teslimat adresi bilgisi bulunamadı.</p>
                        )}
                    </div>

                    {/* Kargo Bilgileri */}
                    {(detailedOrder.shipping_company || detailedOrder.tracking_number) && (
                        <div className="mb-4 p-4 border rounded-lg bg-green-50 text-sm">
                            <h4 className="font-semibold text-green-800 mb-2">Kargo Bilgileri</h4>
                            {detailedOrder.shipping_company && (
                                <p><span className='font-semibold'>Kargo Firması:</span> {detailedOrder.shipping_company}</p>
                            )}
                            {detailedOrder.tracking_number && (
                                <p><span className='font-semibold'>Takip No:</span> <span className="font-mono bg-white px-2 py-1 rounded">{detailedOrder.tracking_number}</span></p>
                            )}
                            {detailedOrder.kargonomi_shipment_id && (
                                <p><span className='font-semibold'>Kargonomi Referans:</span> <span className="font-mono bg-blue-50 px-2 py-1 rounded border border-blue-200">{detailedOrder.kargonomi_shipment_id}</span></p>
                            )}
                            {detailedOrder.selected_shipping_provider && (
                                <p><span className='font-semibold'>Seçilen Kargo:</span> {detailedOrder.selected_shipping_provider}</p>
                            )}
                        </div>
                    )}

                    {/* Admin Notları */}
                    {detailedOrder.admin_notes && (
                        <div className="mb-4 p-4 border rounded-lg bg-yellow-50 text-sm">
                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Admin Notları
                            </h4>
                            <p className="text-yellow-700 whitespace-pre-wrap">{detailedOrder.admin_notes}</p>
                        </div>
                    )}

                    {/* İade Bilgileri */}
                    {hasReturnInfo && (
                        <div className="mb-4 p-4 border rounded-lg bg-orange-50 text-sm">
                            <h4 className="font-semibold text-orange-800 mb-2">İade Bilgileri</h4>
                            <div className="space-y-2">
                                <p><span className='font-semibold'>İade Durumu:</span> {statusTranslations[detailedOrder.status]}</p>
                                <p><span className='font-semibold'>İade Nedeni:</span> {returnData.reason}</p>
                                <p><span className='font-semibold'>İade Tarihi:</span> {new Date(returnData.created_at).toLocaleDateString('tr-TR')}</p>
                                
                                {returnData.customer_tracking_number && (
                                    <p><span className='font-semibold'>Müşteri Kargo No:</span> <span className="font-mono bg-white px-2 py-1 rounded">{returnData.customer_tracking_number}</span></p>
                                )}
                                
                                {returnData.return_address && (
                                    <div className="mt-2 p-2 bg-white rounded border">
                                        <p className="font-semibold text-xs text-gray-600 mb-1">İade Adresi:</p>
                                        <p className="text-xs">{returnData.return_address.name}</p>
                                        <p className="text-xs">{returnData.return_address.address_line}</p>
                                        <p className="text-xs">{returnData.return_address.district}, {returnData.return_address.city} {returnData.return_address.postal_code}</p>
                                    </div>
                                )}
                                
                                {returnData.admin_notes && (
                                    <div className="mt-2 p-2 bg-white rounded border-l-4 border-orange-400">
                                        <p className="font-semibold text-xs text-gray-600 mb-1">Admin Notları:</p>
                                        <p className="text-xs">{returnData.admin_notes}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Sipariş İçeriği */}
                    <div className="mb-4">
                        <h4 className="font-semibold mb-3 text-gray-800">Sipariş İçeriği</h4>
                        <div className='space-y-3 max-h-64 overflow-y-auto border rounded-lg p-3'>
                            {detailedOrder.order_items && detailedOrder.order_items.map((item, index) => (
                                <div key={index} className="flex items-center justify-between border-b pb-3 last:border-b-0">
                                    <div className='flex items-center gap-3'>
                                        <img src={item.products?.main_image_url || 'https://placehold.co/100x100'} alt={item.products?.name || 'Ürün'} className='w-12 h-12 rounded object-cover' />
                                        <div>
                                            <p className='font-semibold text-gray-800'>{item.products?.name || 'Silinmiş Ürün'}</p>
                                            <p className='text-xs text-gray-500'>{item.variant_details_at_purchase?.size} - {item.variant_details_at_purchase?.color}</p>
                                            <p className='text-xs text-gray-500'>Adet: {item.quantity}</p>
                                        </div>
                                    </div>
                                    <p className='font-medium text-gray-800'>₺{parseFloat(item.price_at_purchase).toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    
                    <button onClick={closeModal} className="w-full px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-md transition-colors">Kapat</button>
                </div>
            );
        } catch (error) {
            console.error("Sipariş detayları hatası:", error);
            toast.error("Sipariş detayları getirilemedi: " + (error.message || 'Bilinmeyen hata'));
        }
    }, [openModal, closeModal, token]);
    
    const fetchAllOrders = useCallback(async (currentSearchTerm = '') => {
        if (!token) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (currentSearchTerm) {
                params.set('search', currentSearchTerm);
            } else {
                params.set('startDate', startDate);
                params.set('endDate', endDate);
                params.set('showHidden', showHidden);
            }

            console.log('Sipariş listesi çekiliyor:', params.toString());
            const data = await api(`/api/orders/all?${params.toString()}`, { headers: { 'Authorization': `Bearer ${token}` } });
            console.log('Çekilen sipariş sayısı:', data.length);

            // Shipments tablosundan gelen referans ve takip numaralarını orders verisine ekle
            const normalized = Array.isArray(data) ? data.map(order => {
                // Shipments tablosundan referans ve takip numaralarını al
                const shipment = order.shipments && order.shipments[0]; // İlk shipment'ı al
                
                // İade durumunu kontrol et ve sipariş durumunu güncelle
                let displayStatus = order.status;
                let hasActiveReturn = false;
                
                if (order.returns && order.returns.length > 0) {
                    // En son iade talebinin durumunu al
                    const latestReturn = order.returns.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
                    hasActiveReturn = true;
                    
                    // İade durumuna göre sipariş durumunu güncelle
                    switch (latestReturn.status) {
                        case 'pending_approval':
                            displayStatus = 'return_requested';
                            break;
                        case 'approved':
                            displayStatus = 'return_approved';
                            break;
                        case 'rejected':
                            displayStatus = 'return_rejected';
                            break;
                        case 'shipping_label_created':
                        case 'customer_shipped':
                        case 'in_transit':
                            displayStatus = 'return_shipped';
                            break;
                        case 'received':
                        case 'under_review':
                        case 'review_completed':
                            displayStatus = 'return_received';
                            break;
                        case 'refund_approved':
                        case 'refund_completed':
                        case 'completed':
                            displayStatus = 'return_completed';
                            break;
                        case 'cancelled':
                            // İade iptal edilmişse orijinal sipariş durumunu koru
                            displayStatus = order.status;
                            hasActiveReturn = false;
                            break;
                        default:
                            displayStatus = 'return_requested';
                    }
                }
                
                return {
                    ...order,
                    // Sipariş durumunu iade durumuna göre güncelle
                    status: displayStatus,
                    original_status: order.status, // Orijinal durumu sakla
                    has_active_return: hasActiveReturn,
                    return_info: order.returns && order.returns.length > 0 ? order.returns[0] : null,
                    // Shipments'tan gelen referans numarasını kullan
                    reference_number: shipment?.reference_number || shipment?.shipping_webservice_order_id || null,
                    // Takip numarası öncelik sırası: shipping_webservice_tracking_code > tracking_number > orders.tracking_number
                    tracking_number: shipment?.shipping_webservice_tracking_code || shipment?.tracking_number || order.tracking_number || null
                };
            }) : [];

            if (currentSearchTerm) {
                openModal(<SearchResultsModal results={normalized} searchTerm={currentSearchTerm} closeModal={closeModal} viewOrderDetails={viewOrderDetails} />);
            } else {
                setOrders(normalized);
            }
        } catch (err) { 
            console.error('Sipariş listesi hatası:', err);
            toast.error(err.message); 
        } 
        finally { setLoading(false); }
    }, [token, startDate, endDate, showHidden, openModal, closeModal, viewOrderDetails]);

    useEffect(() => {
        fetchAllOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger]);

    // Real-time: SmartTracking servisinden gelen olaylarla sipariş listesini senkronize et
    // Hooks kurallarına uymak için hook'ları component gövdesinde ve koşulsuz çağırıyoruz.
    const { useSocketEvent } = useSocket();

    // Referans/track güncellemeleri
    useSocketEvent('tracking:updated', (payload) => {
        try {
            const { orderId, shipment } = payload || {};
            if (!orderId) return;
            setOrders(prev => prev.map(o => {
                if (o.id !== orderId) return o;
                const incomingRef = shipment?.shipping_webservice_order_id;
                const incomingTrack = shipment?.shipping_webservice_tracking_code;
                return {
                    ...o,
                    // Referans her zaman referans alanına
                    reference_number: incomingRef || o.reference_number || null,
                    // Takip yalnızca gerçek takip kodu geldiyse yazılsın (referans ile aynıysa yazma)
                    tracking_number: (incomingTrack && incomingTrack !== (incomingRef || o.reference_number)) 
                        ? incomingTrack 
                        : o.tracking_number || null
                };
            }));
        } catch (e) {
            console.warn('[Orders] tracking:updated patch hatası:', e);
        }
    });

    // Tamamlanma
    useSocketEvent('tracking:completed', (payload) => {
        try {
            const { orderId, shipment } = payload || {};
            if (!orderId) return;
            setOrders(prev => prev.map(o => {
                if (o.id !== orderId) return o;
                const incomingRef = shipment?.shipping_webservice_order_id;
                const incomingTrack = shipment?.shipping_webservice_tracking_code;
                return {
                    ...o,
                    reference_number: incomingRef || o.reference_number || null,
                    tracking_number: (incomingTrack && incomingTrack !== (incomingRef || o.reference_number))
                        ? incomingTrack
                        : o.tracking_number || null
                };
            }));
        } catch (e) {
            console.warn('[Orders] tracking:completed patch hatası:', e);
        }
    });

    // Hata
    useSocketEvent('tracking:error', (payload) => {
        try {
            if (payload?.orderId && payload?.error) {
                console.warn(`[Orders] tracking:error (order ${payload.orderId}):`, payload.error);
            }
        } catch {}
    });

    // Kontrollü otomatik yenileme
    useEffect(() => {
        if (!autoRefresh) return;
        
        const interval = setInterval(() => {
            if (!loading) {
                fetchAllOrders();
            }
        }, 60000); // 1 dakika

        return () => clearInterval(interval);
    }, [fetchAllOrders, loading, autoRefresh]);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setSearchTerm('');
        setRefreshTrigger(t => t + 1);
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchAllOrders(searchTerm.trim());
    };
    
    const updateOrder = useCallback(async (orderId, updates) => {
        try {
            // Sipariş güncellemesinin güvenli bir kopyasını oluştur
            const updateData = { ...updates };

            // 'Teslim Edildi' durumu için güncellemede özel işlemi kaldırıyoruz
            // Sunucu tarafında sorun yaratıyor olabilir
            // Backend'in zaten bu güncellemeyi yaptığını varsayalım

            // Güncelleştirme isteğini gönderirken daha detaylı hata yakalama
            console.log('Sipariş güncelleme verileri:', updateData);
            const response = await api.put(`/api/orders/${orderId}`, updateData, {
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            });
            console.log('Sipariş güncelleme başarılı:', response);

            toast.success('Sipariş güncellendi!');
            setRefreshTrigger(t => t + 1);
        } catch (err) { 
            console.error('Sipariş güncelleme hatası:', err);
            toast.error(`Güncelleme hatası: ${err.response?.error || err.message || 'Bilinmeyen hata'}`);
        }
    }, [token]);

    const handleCreateShipment = useCallback(async (orderId) => {
        try {
            console.log(`Sipariş ${orderId} için kargo oluşturuluyor...`);
            toast.info('Kargo oluşturuluyor, lütfen bekleyin...');
            
            const response = await api.post(`/api/orders/${orderId}/auto-shipping`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            console.log('Kargo oluşturma yanıtı:', response);
            
            if (response.success) {
                if (response.isConfirmed) {
                    toast.success(`🎉 Kargo başarıyla oluşturuldu! Takip kodu: ${response.tracking_code}`);
                } else if (response.requiresManualConfirmation) {
                    // Özel durum: Yeterli bakiye yok
                    if (response.error_message && response.error_message.includes('Yeterli Bakiye')) {
                        toast.error(`❌ Kargonomi Bakiye Hatası: ${response.error_message}\n\n🔧 Çözüm: Kargonomi panelinden bakiye yükleyin.`);
                    } else {
                        toast.warning(`⚠️ Kargo draft oluşturuldu. Manuel onay gerekiyor.\n\nHata: ${response.error_message || 'Bilinmiyor'}`);
                    }
                } else if (response.warning) {
                    toast.warning(`⚠️ ${response.warning}\n\nDetay: ${response.details || response.action_required || 'Bilinmiyor'}`);
                } else {
                    toast.success('✅ Kargo oluşturuldu!');
                }
                
                // Sipariş listesini yenile
                setRefreshTrigger(t => t + 1);
            } else {
                throw new Error(response.error || 'Kargo oluşturulamadı');
            }
        } catch (err) {
            console.error('Kargo oluşturma hatası:', err);
            toast.error(`Kargo oluşturma hatası: ${err.response?.data?.error || err.message || 'Bilinmeyen hata'}`);
        }
    }, [token]);
    
    const showDeleteConfirm = useCallback((order) => {
        openModal(
            <div className="p-4 text-center max-w-md mx-auto">
                <ArchiveBoxXMarkIcon className="mx-auto h-12 w-12 text-red-600" />
                <h3 className="mt-2 text-lg font-medium">Siparişi Sil</h3>
                <p className="mt-2 text-sm text-gray-500">
                    {order.shipping_name} adına kayıtlı MINELA-{order.order_number} numaralı siparişi kalıcı olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                </p>
                <div className="mt-6 flex justify-center space-x-3">
                    <button onClick={closeModal} className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300">İptal</button>
                    <button onClick={async () => {
                        closeModal();
                        try {
                            await api(`/api/orders/${order.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }});
                            toast.success("Sipariş kalıcı olarak silindi.");
                            setRefreshTrigger(t => t + 1);
                        } catch (err) { toast.error(err.message); }
                    }} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700">Evet, Sil</button>
                </div>
            </div>
        );
    }, [token, openModal, closeModal]);

    const handleStatusChange = useCallback((orderId, newStatus) => {
        const currentOrder = orders.find(o => o.id === orderId);

        if (newStatus === 'shipped') {
            // Kargolandı durumu için özel modal
            openModal(<TrackingInfoModal order={currentOrder} onSave={updateOrder} closeModal={closeModal} />);
        } else if (newStatus === 'delivered') {
            // Teslim Edildi durumu için özel işleme
            // Arka uçtaki hatayı önlemek için sadece durum bilgisini gönderelim
            try {
                updateOrder(orderId, { status: newStatus, skip_updated_at: true });
            } catch (err) {
                console.error('Teslim edildi durumu güncellenirken hata:', err);
                toast.error('Sipariş durumu güncellenemedi. Tekrar deneyin.');
            }
        } else {
            // Diğer durumlar için normal güncelleme
            updateOrder(orderId, { status: newStatus });
        }
    }, [orders, openModal, closeModal, updateOrder]);
    
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const handlePrintOrder = (orderId) => {
        window.open(`/print-order/${orderId}`, '_blank');
    };


    const handlePrintBarcode = async (order) => {
        try {
            console.log('[PrintBarcode] Barkod yazdırma başlatılıyor, order:', order.id);
            
            // Kargonomi API'den doğru barkod bilgisini al
            const barcodeResponse = await api.get(`/api/kargonomi/order-barcode/${order.id}`);
            
            console.log('[PrintBarcode] Backend yanıtı:', barcodeResponse);
            
            if (!barcodeResponse.success) {
                throw new Error(barcodeResponse.error || 'Barkod bilgisi alınamadı');
            }
            
            const barcodeData = barcodeResponse.data;
            
            // Barkod değerini kontrol et
            if (!barcodeData.barcodeValue && !barcodeData.barcodeNumber) {
                throw new Error('Barkod numarası bulunamadı. Kargo henüz oluşturulmamış olabilir.');
            }
            
            // Geriye uyumluluk için her iki alan adını da kontrol et
            const barcodeValue = barcodeData.barcodeValue || barcodeData.barcodeNumber;
            
            console.log('[PrintBarcode] Kullanılacak barkod değeri:', barcodeValue);
            
            // Adres bilgisini güvenli şekilde parse et
            let shippingAddress = {};
            if (order.shipping_address) {
                if (typeof order.shipping_address === 'string') {
                    try {
                        shippingAddress = JSON.parse(order.shipping_address);
                    } catch (e) {
                        console.warn('Adres parse hatası:', e);
                        shippingAddress = {};
                    }
                } else {
                    shippingAddress = order.shipping_address;
                }
            }

            // Barkod tipine göre label belirle
            const getBarcodeLabel = (type) => {
                switch (type) {
                    case 'reference_number': 
                    case 'shipping_webservice_order_id': 
                        return 'Referans Numarası';
                    case 'shipping_webservice_barcode': 
                        return 'Kargo Barkodu';
                    case 'tracking_code': 
                    case 'shipping_webservice_tracking_code':
                        return 'Takip Kodu';
                    case 'kargonomi_shipment_id': 
                        return 'Kargonomi ID';
                    default: 
                        return 'Barkod';
                }
            };

            const printContent = `
                <!DOCTYPE html>
                <html>
                    <head>
                        <title>Kargo Barkodu - ${order.order_number}</title>
                        <meta charset="UTF-8">
                        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
                        <style>
                            body { 
                                font-family: Arial, sans-serif; 
                                padding: 20px; 
                                text-align: center; 
                                margin: 0;
                            }
                            .barcode-container { 
                                margin: 20px 0; 
                                display: flex;
                                justify-content: center;
                                align-items: center;
                                flex-direction: column;
                            }
                            .order-info { 
                                margin: 10px 0; 
                                font-size: 14px; 
                            }
                            .reference-number { 
                                font-size: 18px; 
                                font-weight: bold; 
                                margin: 15px 0;
                                padding: 10px;
                                background: #f0f0f0;
                                border-radius: 5px;
                                font-family: monospace;
                            }
                            .company-header {
                                border-bottom: 2px solid #10B981;
                                padding-bottom: 10px;
                                margin-bottom: 20px;
                            }
                            .address-info {
                                text-align: left;
                                margin: 15px 0;
                                padding: 10px;
                                border: 1px solid #ddd;
                                border-radius: 5px;
                            }
                            .barcode-info {
                                margin: 10px 0;
                                padding: 10px;
                                background: #f8f9fa;
                                border-radius: 5px;
                                border: 1px solid #dee2e6;
                            }
                            .barcode-type {
                                font-size: 12px;
                                color: #6c757d;
                                margin-top: 5px;
                            }
                            @media print { 
                                body { margin: 0; padding: 10px; }
                                .no-print { display: none !important; }
                            }
                        </style>
                    </head>
                    <body>
                        <div class="company-header">
                            <h2>MINELA KIDS - KARGO ETİKETİ</h2>
                        </div>
                        
                        <div class="order-info">
                            <p><strong>Sipariş No:</strong> MINELA-${order.order_number}</p>
                            <p><strong>Tarih:</strong> ${new Date(order.created_at).toLocaleDateString('tr-TR')}</p>
                            <p><strong>Kargo Firması:</strong> ${barcodeData.shippingProvider || 'Belirtilmemiş'}</p>
                        </div>

                        <div class="address-info">
                            <h3>Teslimat Bilgileri</h3>
                            <p><strong>Alıcı:</strong> ${shippingAddress.full_name || 'Belirtilmemiş'}</p>
                            <p><strong>Telefon:</strong> ${shippingAddress.phone || 'Belirtilmemiş'}</p>
                            <p><strong>Adres:</strong> ${shippingAddress.address_line || 'Belirtilmemiş'}</p>
                            <p><strong>İl/İlçe:</strong> ${shippingAddress.city || ''} / ${shippingAddress.district || ''}</p>
                            ${shippingAddress.neighborhood ? `<p><strong>Mahalle:</strong> ${shippingAddress.neighborhood}</p>` : ''}
                        </div>
                        
                        <div class="barcode-container">
                            <h3>${getBarcodeLabel(barcodeData.barcodeType)}</h3>
                            <svg id="barcode"></svg>
                            <div class="reference-number">${barcodeValue}</div>
                            <div class="barcode-type">Barkod Tipi: ${barcodeData.barcodeType}</div>
                        </div>
                        
                        <div class="barcode-info">
                            <p><strong>Referans No:</strong> ${barcodeData.referenceNumber || 'Bekleniyor'}</p>
                            <p><strong>Takip Kodu:</strong> ${barcodeData.trackingCode || 'Bekleniyor'}</p>
                            <p><strong>Kargo Barkodu:</strong> ${barcodeData.shippingWebserviceBarcode || 'Bekleniyor'}</p>
                        </div>
                        
                        <script>
                            // Barkod oluşturma
                            try {
                                JsBarcode("#barcode", "${barcodeValue}", {
                                    format: "CODE128",
                                    width: 3,
                                    height: 100,
                                    displayValue: false,
                                    fontSize: 16,
                                    margin: 10
                                });
                            } catch (error) {
                                console.error('Barkod oluşturma hatası:', error);
                                document.getElementById('barcode').innerHTML = '<p style="color: red;">Barkod oluşturulamadı: ' + error.message + '</p>';
                            }
                        </script>
                    </body>
                </html>
            `;

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                throw new Error('Pop-up engellendi. Lütfen pop-up engelleyiciyi kapatın.');
            }
            
            printWindow.document.write(printContent);
            printWindow.document.close();
            
            // Barkod yüklendikten sonra yazdır
            setTimeout(() => {
                try {
                    printWindow.print();
                } catch (printError) {
                    console.error('Yazdırma hatası:', printError);
                    alert('Yazdırma hatası: ' + printError.message);
                }
            }, 1000); // 1 saniye bekle

        } catch (error) {
            console.error('Barkod yazdırma hatası:', error);
            alert('Barkod yazdırılırken hata oluştu: ' + error.message);
        }
    };

    return (
        <div>
            <form onSubmit={handleFilterSubmit} className="p-4 bg-gray-50 rounded-lg mb-4 flex flex-col md:flex-row items-end gap-4 border">
                <div className="flex-grow grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div> <label className="text-xs font-semibold text-gray-600">Başlangıç Tarihi</label> <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full mt-1 p-2 border rounded-md text-sm"/> </div>
                    <div> <label className="text-xs font-semibold text-gray-600">Bitiş Tarihi</label> <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full mt-1 p-2 border rounded-md text-sm"/> </div>
                </div>
                <div className="flex flex-col gap-2">
                    <div className="flex items-center">
                        <input type="checkbox" id="showHidden" checked={showHidden} onChange={e => setShowHidden(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"/>
                        <label htmlFor="showHidden" className="ml-2 text-sm text-gray-600">Gizlenenleri Göster</label>
                    </div>
                    <div className="flex items-center">
                        <input type="checkbox" id="autoRefresh" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"/>
                        <label htmlFor="autoRefresh" className="ml-2 text-sm text-gray-600">Otomatik Yenile</label>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button type="submit" className="bg-gradient-to-r from-brand-green to-brand-green-light text-white px-6 py-3 rounded-2xl text-sm font-bold hover:from-brand-green-light hover:to-brand-green transition-all duration-300 transform hover:scale-105 shadow-xl w-full md:w-auto">
                        <span className="flex items-center justify-center">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            Filtrele
                        </span>
                    </button>
                    <button type="button" onClick={() => {
                        setStartDate('');
                        setEndDate('');
                        setShowHidden(false);
                        setRefreshTrigger(t => t + 1);
                    }} className="bg-purple-500 text-white px-4 py-2 rounded-md text-sm hover:bg-purple-600 w-full md:w-auto">Tümünü Göster</button>
                    <button type="button" onClick={() => setRefreshTrigger(t => t + 1)} className="bg-green-500 text-white px-4 py-2 rounded-md text-sm hover:bg-green-600 w-full md:w-auto">🔄 Yenile</button>
                </div>
            </form>
            
            <form onSubmit={handleSearchSubmit} className="mb-4">
                <div className="relative">
                    <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full p-2 pl-10 border rounded-md text-sm" placeholder="Sipariş No veya Müşteri Adı ile Ara..."/>
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"/>
                </div>
            </form>

            {/* Masaüstü Görünüm */}
            <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sipariş</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Müşteri</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Toplam</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {loading ? (
                            <tr><td colSpan="5" className="text-center p-10">Siparişler yükleniyor...</td></tr>
                        ) : orders.length === 0 ? (
                            <tr><td colSpan="5" className="text-center p-10 text-gray-500">Bu kriterlere uygun sipariş bulunamadı.</td></tr>
                        ) : orders.map(order => (
                            <tr key={order.id} className={`${order.is_hidden ? 'opacity-50 bg-gray-100' : ''} hover:bg-gray-50`}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    <p className='font-semibold text-gray-800'>MINELA-{order.order_number}</p>
                                    <p className='text-xs'>{formatDate(order.created_at)}</p>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{order.shipping_name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">₺{parseFloat(order.total_price).toFixed(2)}</td>
                                <td className="px-6 py-4 text-sm w-1/3 min-w-[250px]">
                                    <select 
                                        value={order.status} 
                                        onChange={(e) => handleStatusChange(order.id, e.target.value)} 
                                        className={`w-full p-2 border rounded-md text-xs font-semibold ${statusColors[order.status]} border`}
                                        disabled={order.has_active_return && !order.status.startsWith('return_')}
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{statusTranslations[status]}</option>
                                        ))}
                                    </select>
                                    {/* İade Durumu Gösterimi */}
                                    {order.has_active_return && (
                                        <div className="mt-2 p-2 bg-orange-50 border border-orange-200 rounded text-xs whitespace-normal break-words">
                                            <p className="font-semibold text-orange-800 mb-1">🔄 Aktif İade Talebi</p>
                                            <p><span className="font-medium">İade Nedeni:</span> {order.return_info?.reason || 'Belirtilmemiş'}</p>
                                            <p><span className="font-medium">İade Tarihi:</span> {order.return_info?.created_at ? new Date(order.return_info.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}</p>
                                            {order.return_info?.admin_notes && (
                                                <p className="mt-1"><span className="font-medium">Admin Notu:</span> {order.return_info.admin_notes}</p>
                                            )}
                                        </div>
                                    )}
                                    {/* Kargo Bilgileri (4 satır) */}
                                    {(order.shipping_company || order.tracking_number || order.kargonomi_shipment_id || order.reference_number) && (
                                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs space-y-1 whitespace-normal break-words">
                                            <p><span className="font-medium">Kargo:</span> {order.shipping_company || order.selected_shipping_provider || 'Kargo firması bekleniyor'}</p>
                                            <p><span className="font-medium">Kargonomi id:</span> {order.kargonomi_shipment_id || 'ID bekleniyor'}</p>
                                            <p>
                                                <span className="font-medium">Referans numarası:</span>{' '}
                                                {order.reference_number || 'Referans numarası bekleniyor'}
                                            </p>
                                            <p>
                                                <span className="font-medium">Takip numarası:</span>{' '}
                                                {order.tracking_number || 'Takip numarası bekleniyor'}
                                            </p>
                                        </div>
                                    )}
                                    {/* Takip Durumu Gösterimi */}
                                    {(order.kargonomi_shipment_id && !order.reference_number) && (
                                        <p className='text-xs mt-1 text-yellow-600 truncate flex items-center'>
                                            <div className="animate-spin rounded-full h-2 w-2 border border-yellow-500 border-t-transparent mr-1"></div>
                                            ⏳ Referans bekleniyor...
                                        </p>
                                    )}
                                    {(order.reference_number && (!order.tracking_number || order.tracking_number === order.reference_number)) && (
                                        <p className='text-xs mt-1 text-blue-600 truncate flex items-center'>
                                            <div className="animate-spin rounded-full h-2 w-2 border border-blue-500 border-t-transparent mr-1"></div>
                                            ⏳ Takip bekleniyor...
                                        </p>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                                    <div className="flex justify-end space-x-1">
                                        <button onClick={() => handlePrintOrder(order.id)} className="p-2 rounded-full hover:bg-gray-100 inline-flex items-center" title="Sipariş Yazdır">
                                            <PrinterIcon className="h-5 w-5 text-gray-700" />
                                        </button>
                                        {order.kargonomi_shipment_id && (
                                            <button onClick={() => handlePrintBarcode(order)} className="p-2 rounded-full hover:bg-blue-100 inline-flex items-center" title="Barkod Yazdır">
                                                <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                                                </svg>
                                            </button>
                                        )}
                                        <button onClick={() => viewOrderDetails(order.id)} className="p-2 rounded-full hover:bg-gray-100 inline-flex items-center" title="Detayları Gör">
                                            <EyeIcon className="h-5 w-5 text-brand-green" />
                                        </button>
                                        <button onClick={() => updateOrder(order.id, { is_hidden: !order.is_hidden })} className="p-2 rounded-full hover:bg-gray-100 inline-flex items-center" title={order.is_hidden ? 'Siparişi Göster' : 'Siparişi Gizle'}>
                                            {order.is_hidden ? <EyeIcon className="h-5 w-5 text-gray-500" /> : <EyeSlashIcon className="h-5 w-5 text-gray-500" />}
                                        </button>
                                        <button onClick={() => showDeleteConfirm(order)} className="p-2 rounded-full hover:bg-gray-100 inline-flex items-center" title="Siparişi Sil">
                                            <TrashIcon className="h-5 w-5 text-red-600" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobil Görünüm */}
            <div className="lg:hidden space-y-4">
                {loading ? (
                    <div className="text-center p-10 bg-white rounded-lg shadow-sm">Siparişler yükleniyor...</div>
                ) : orders.length === 0 ? (
                    <div className="text-center p-10 bg-white rounded-lg shadow-sm text-gray-500">Bu kriterlere uygun sipariş bulunamadı.</div>
                ) : orders.map(order => (
                    <div key={order.id} className={`bg-white rounded-lg shadow-sm border ${order.is_hidden ? 'opacity-50 bg-gray-50' : ''}`}>
                        <div className="p-4">
                            {/* Üst Kısım - Sipariş Bilgileri */}
                            <div className="flex justify-between items-start mb-3">
                                <div>
                                    <p className="font-semibold text-gray-800">MINELA-{order.order_number}</p>
                                    <p className="text-xs text-gray-500">{formatDate(order.created_at)}</p>
                                    <p className="text-sm text-gray-600 mt-1">{order.shipping_name}</p>
                                </div>
                                <div className="text-right">
                                    <p className="font-bold text-gray-900">₺{parseFloat(order.total_price).toFixed(2)}</p>
                                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border mt-1 ${statusColors[order.status]}`}>
                                        {statusTranslations[order.status]}
                                    </span>
                                </div>
                            </div>

                            {/* Durum Değiştirme */}
                            <div className="mb-3">
                                <label className="block text-xs font-medium text-gray-700 mb-1">Durum Güncelle:</label>
                                <select 
                                    value={order.status} 
                                    onChange={(e) => handleStatusChange(order.id, e.target.value)} 
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                    disabled={order.has_active_return && !order.status.startsWith('return_')}
                                >
                                    {statusOptions.map(status => (
                                        <option key={status} value={status}>{statusTranslations[status]}</option>
                                    ))}
                                </select>
                            </div>

                            {/* İade Durumu Gösterimi - Mobil */}
                            {order.has_active_return && (
                                <div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <h4 className="font-semibold text-orange-800 mb-2 flex items-center">
                                        🔄 Aktif İade Talebi
                                    </h4>
                                    <div className="space-y-1 text-xs">
                                        <div><span className="font-medium">İade Nedeni:</span> {order.return_info?.reason || 'Belirtilmemiş'}</div>
                                        <div><span className="font-medium">İade Tarihi:</span> {order.return_info?.created_at ? new Date(order.return_info.created_at).toLocaleDateString('tr-TR') : 'Bilinmiyor'}</div>
                                        {order.return_info?.admin_notes && (
                                            <div><span className="font-medium">Admin Notu:</span> {order.return_info.admin_notes}</div>
                                        )}
                                        {order.return_info?.customer_tracking_number && (
                                            <div><span className="font-medium">Müşteri Kargo No:</span> {order.return_info.customer_tracking_number}</div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Kargo Bilgileri (4 satır) */}
                            {(order.shipping_company || order.tracking_number || order.kargonomi_shipment_id || order.reference_number) && (
                                <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
                                    <div><span className="font-medium">Kargo:</span> {order.shipping_company || order.selected_shipping_provider || 'Kargo firması bekleniyor'}</div>
                                    <div><span className="font-medium">Kargonomi id:</span> {order.kargonomi_shipment_id || 'ID bekleniyor'}</div>
                                    <div>
                                        <span className="font-medium">Referans numarası:</span>{' '}
                                        {order.reference_number || 'Referans numarası bekleniyor'}
                                    </div>
                                    <div>
                                        <span className="font-medium">Takip numarası:</span>{' '}
                                        {order.tracking_number || 'Takip numarası bekleniyor'}
                                    </div>
                                </div>
                            )}

                            {/* İşlem Butonları */}
                            <div className="flex justify-between items-center pt-3 border-t">
                                <div className="flex space-x-2">
                                    <button onClick={() => handlePrintOrder(order.id)} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors" title="Sipariş Yazdır">
                                        <PrinterIcon className="h-4 w-4 text-gray-700" />
                                    </button>
                                    {order.kargonomi_shipment_id && (
                                        <button onClick={() => handlePrintBarcode(order)} className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 transition-colors" title="Barkod Yazdır">
                                            <svg className="h-4 w-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V6a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1zm12 0h2a1 1 0 001-1V6a1 1 0 00-1-1h-2a1 1 0 00-1 1v1a1 1 0 001 1zM5 20h2a1 1 0 001-1v-1a1 1 0 00-1-1H5a1 1 0 00-1 1v1a1 1 0 001 1z" />
                                            </svg>
                                        </button>
                                    )}
                                    <button onClick={() => viewOrderDetails(order.id)} className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 transition-colors" title="Detayları Gör">
                                        <EyeIcon className="h-4 w-4 text-blue-600" />
                                    </button>
                                    {(order.status === 'pending' || order.status === 'confirmed') && (
                                        <button 
                                            onClick={() => handleCreateShipment(order.id)} 
                                            className="p-2 rounded-lg bg-green-100 hover:bg-green-200 transition-colors" 
                                            title="Kargo Oluştur"
                                        >
                                            <svg className="h-4 w-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                                <div className="flex space-x-2">
                                    <button onClick={() => updateOrder(order.id, { is_hidden: !order.is_hidden })} className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors" title={order.is_hidden ? 'Siparişi Göster' : 'Siparişi Gizle'}>
                                        {order.is_hidden ? <EyeIcon className="h-4 w-4 text-gray-500" /> : <EyeSlashIcon className="h-4 w-4 text-gray-500" />}
                                    </button>
                                    <button onClick={() => showDeleteConfirm(order)} className="p-2 rounded-lg bg-red-100 hover:bg-red-200 transition-colors" title="Siparişi Sil">
                                        <TrashIcon className="h-4 w-4 text-red-600" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default OrderManagementPanel;
